import { toast } from 'react-hot-toast';
import {
  Check,
  Shield,
  CheckCircle2,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import subscriptionData from '../../../../../data/subscription/subscriptionData.json';

const PLAN_COPY = subscriptionData.planCopy;
const PLAN_FEATURES = subscriptionData.planFeatures;

const SubscriptionPlanCard = ({
  plan,
  billingInterval,
  subscription,
  isPaidActive,
  currentPlanSlug,
  currentInterval,
  daysRemaining,
  slugRanks,
  currentPlanRank,
  subscribing,
  handleSubscribe,
  handleUpgradeClick,
  handleDirectDowngradeRequest,
  handleEnterpriseRequest,
  formatPrice,
  navigate
}) => {
  const isExactCurrent = isPaidActive && currentPlanSlug === plan.slug && (currentInterval === billingInterval || (!subscription?.billing_interval && billingInterval === 'month'));
  const isRecommended = plan.slug === 'pro' && currentPlanSlug !== 'growth';
  const includedFeatures = PLAN_FEATURES[plan.slug] || (plan.features || []).filter((feature) => feature.included).map((feature) => feature.name).slice(0, 5);

  const yearlyPrice = plan.yearly_price || Math.round(plan.monthly_price * 12 * 0.80);
  const monthlyEquivalent = plan.annual_monthly_equivalent || Math.round(yearlyPrice / 12);
  const annualSavings = plan.annual_savings || ((plan.monthly_price * 12) - yearlyPrice);

  const pendingChange = subscription?.pending_change;
  const isApprovedPending = Boolean(
    pendingChange &&
    (pendingChange.status === 'mandate_pending' || pendingChange.status === 'approved')
  );
  const pendingTargetPlanId = pendingChange?.target_plan_id?._id || pendingChange?.target_plan_id;
  const isApprovedTargetPlan = Boolean(
    isApprovedPending &&
    pendingTargetPlanId &&
    String(pendingTargetPlanId) === String(plan._id)
  );
  const approvedTargetInterval = pendingChange?.target_billing_interval || 'month';

  const upcomingPlanId = subscription?.upcoming_plan_id?._id || subscription?.upcoming_plan_id;
  const isUpcomingPlan = subscription?.status === 'trial' && (
    (upcomingPlanId && String(upcomingPlanId) === String(plan._id)) ||
    subscription?.upcoming_plan_id?.slug === plan.slug
  );

  return (
    <article 
      className={`relative flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition-all hover:shadow-md ${
        isApprovedTargetPlan
          ? 'border-[#067978] border-2 bg-teal-50/30 shadow-[0_8px_22px_rgba(6,121,120,0.18)]'
          : isUpcomingPlan
          ? 'border-emerald-500 border-2 bg-emerald-50/20 shadow-[0_8px_22px_rgba(16,185,129,0.12)]'
          : isExactCurrent 
          ? 'border-[#067978] bg-teal-50/35 shadow-[0_8px_22px_rgba(6,121,120,0.12)]' 
          : isRecommended 
          ? 'border-[#067978] border-2' 
          : 'border-slate-200'
      }`}
    >
      {/* Floating Top Left Badge (Approved / Upcoming / Current / Cancelled / Recommended) */}
      {(isApprovedTargetPlan || isUpcomingPlan || isExactCurrent || isRecommended || (subscription?.status === 'cancelled' && currentPlanSlug === plan.slug)) && (
        <span className={`absolute -top-3 left-5 z-10 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg ${
          isApprovedTargetPlan
            ? 'bg-gradient-to-r from-teal-700 to-emerald-600 shadow-teal-700/30'
            : isUpcomingPlan
            ? 'bg-emerald-600 shadow-emerald-600/30'
            : subscription?.status === 'cancelled' && currentPlanSlug === plan.slug
            ? 'bg-amber-600 shadow-amber-600/30'
            : 'bg-[#067978] shadow-[#067978]/30'
        }`}>
          {isApprovedTargetPlan
            ? '★ Approved Plan Change'
            : isUpcomingPlan
            ? '★ Next Active Plan'
            : subscription?.status === 'cancelled' && currentPlanSlug === plan.slug
            ? `Cancelled · Ends in ${daysRemaining} days`
            : isExactCurrent ? 'Current plan' : 'Recommended'
          }
        </span>
      )}

      {/* Floating Top Right Badge (Save 20% / Amount) */}
      {billingInterval === 'year' && !plan.is_custom && (
        <span className="absolute -top-3 right-5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-0.5 text-[11px] font-normal text-emerald-800 shadow-xs">
          Save {formatPrice(annualSavings)}/yr
        </span>
      )}

      <div className="min-h-[100px] pt-1">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">{plan.name}</h2>
        </div>
        <p className="mt-2 text-sm leading-5 text-slate-500">
          {PLAN_COPY[plan.slug] || plan.description || 'A plan tailored for your business.'}
        </p>
      </div>

      <div className="border-y border-slate-100 py-4 my-4">
        {plan.is_custom ? (
          <div className="text-3xl font-semibold text-[#067978]">Let&apos;s talk</div>
        ) : billingInterval === 'year' ? (
          <>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-3xl font-semibold text-[#067978]">{formatPrice(monthlyEquivalent)}</span>
              <span className="text-xs font-medium text-slate-400">/ month</span>
              <span className="text-xs font-medium text-slate-500 ml-0.5">+ {plan.gst_percentage ?? 18}% GST</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Billed annually ({formatPrice(yearlyPrice)}/yr)
            </p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5 flex-wrap">
              <span className="text-3xl font-semibold text-[#067978]">{formatPrice(plan.monthly_price)}</span>
              <span className="text-xs font-medium text-slate-400">/ month</span>
              <span className="text-xs font-medium text-slate-500 ml-0.5">+ {plan.gst_percentage ?? 18}% GST</span>
            </div>
          </>
        )}
      </div>

      <ul className="space-y-2.5 flex-1 mb-5">
        {includedFeatures.map((feature) => (
          <li key={feature} className="flex gap-2 text-xs leading-5 text-slate-600">
            <Check className="w-4 h-4 shrink-0 mt-0.5 text-[#067978] stroke-[2.5]" />
            {feature}
          </li>
        ))}
      </ul>

      {(() => {
        if (isUpcomingPlan) {
          return (
            <div className="w-full py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-xs flex justify-center items-center gap-1.5 shadow-2xs">
              <CheckCircle2 size={15} className="text-emerald-600" />
              <span>Scheduled for Trial End</span>
            </div>
          );
        }

        if (subscription?.status === 'cancelled' && currentPlanSlug === plan.slug) {
          return (
            <button
              onClick={() => navigate(`/checkout?plan=${plan._id}&interval=${billingInterval}&from=subscription&mode=reactivate`)}
              className="w-full py-2.5 rounded-xl font-semibold text-sm bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
            >
              <RefreshCw size={15} />
              <span>Reactivate Plan</span>
            </button>
          );
        }

        if (isExactCurrent) {
          return (
            <div className="w-full py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-[#067978] font-semibold text-sm flex justify-center items-center gap-2">
              <Shield size={15} /> Current plan
            </div>
          );
        }

        const planRank = slugRanks[plan.slug] || plan.display_order || 1;
        const isCurrentAnnual = isPaidActive && currentInterval === 'year';
        const isCurrentMonthly = isPaidActive && currentInterval === 'month';

        // 0. If this is the approved target plan awaiting mandate authorization:
        if (isApprovedTargetPlan) {
          const actionText = `Authorize ${plan.name} (${approvedTargetInterval === 'year' ? 'Annual' : 'Monthly'})`;
          return (
            <button
              onClick={() => {
                const customPriceParam = pendingChange.custom_price ? `&custom_price=${pendingChange.custom_price}` : '';
                const customVoucherParam = pendingChange.custom_voucher_limit ? `&custom_voucher_limit=${pendingChange.custom_voucher_limit}` : '';
                const customTokenParam = pendingChange.custom_link_token ? `&custom_link=${pendingChange.custom_link_token}` : '';
                navigate(`/checkout?plan=${plan._id}&interval=${approvedTargetInterval}&from=subscription${customPriceParam}${customVoucherParam}${customTokenParam}`);
              }}
              className="w-full py-2.5 rounded-2xl font-bold text-sm bg-gradient-to-r from-teal-700 via-[#067978] to-teal-800 text-white shadow-md shadow-teal-900/20 hover:shadow-lg transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
            >
              <span>{actionText}</span>
              <ArrowRight size={16} />
            </button>
          );
        }

        // If another plan change is approved and awaiting mandate authorization:
        if (isApprovedPending && !isApprovedTargetPlan) {
          return (
            <button
              onClick={() => {
                toast.error('You have an approved plan change awaiting authorization. Please authorize it first or contact support.');
              }}
              className="w-full py-2.5 rounded-2xl font-semibold text-xs border border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>Plan Change Pending Authorization</span>
            </button>
          );
        }

        if (plan.is_custom || plan.slug === 'enterprise') {
          return (
            <button
              onClick={() => handleEnterpriseRequest(plan)}
              disabled={subscribing === plan._id}
              className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98 disabled:opacity-60"
            >
              {subscribing === plan._id ? (
                <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Talk to us</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          );
        }

        // 1. If currently on an Annual Plan:
        if (isCurrentAnnual) {
          const isCycleSwitch = billingInterval !== currentInterval;
          const reqType = isCycleSwitch ? 'switch_billing_cycle' : (planRank < currentPlanRank ? 'downgrade' : 'switch_billing_cycle');
          return (
            <button
              onClick={() => handleDirectDowngradeRequest(plan, billingInterval, reqType)}
              disabled={subscribing === plan._id}
              className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98 disabled:opacity-60"
            >
              {subscribing === plan._id ? (
                <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>{isCycleSwitch ? 'Switch to Monthly' : planRank < currentPlanRank ? 'Request Downgrade' : 'Request Plan Change'}</span>
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          );
        }

        // 2. If currently on a Monthly Plan:
        if (isCurrentMonthly) {
          if (billingInterval === 'year') {
            return (
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={subscribing === plan._id}
                className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98 disabled:opacity-60"
              >
                {subscribing === plan._id ? (
                  <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Switch to Annual</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            );
          }

          if (planRank > currentPlanRank) {
            return (
              <button
                onClick={() => handleUpgradeClick(plan)}
                className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <span>Upgrade to {plan.name}</span>
                <ArrowRight size={16} />
              </button>
            );
          }

          if (planRank < currentPlanRank) {
            return (
              <button
                onClick={() => handleDirectDowngradeRequest(plan, billingInterval, 'downgrade')}
                disabled={subscribing === plan._id}
                className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98 disabled:opacity-60"
              >
                {subscribing === plan._id ? (
                  <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Request Downgrade</span>
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            );
          }
        }

        return (
          <button 
            onClick={() => handleSubscribe(plan)} 
            disabled={subscribing === plan._id} 
            className="w-full py-2.5 rounded-2xl font-bold text-sm border-2 border-[#067978] text-[#067978] bg-white hover:bg-teal-50 transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98 disabled:opacity-60"
          >
            {subscribing === plan._id ? (
              <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Choose {plan.name}</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        );
      })()}
    </article>
  );
};

export default SubscriptionPlanCard;
